export const SMALL_SCREEN = '50em';
export const MEDIUM_SCREEN = '52em';
export const LARGE_SCREEN = '68em';
const breakpoints = [SMALL_SCREEN, MEDIUM_SCREEN, LARGE_SCREEN];

export const colors = {
  dangerRed: '#EE5C3D',
  grayscale: {
    black: '#000000',
    stone: '#787878',
    charcoal: '#333333',
    gray: '#C4C4C4',
    lightGray: '#EBEBEB',
    lightestGray: '#EFEFEF',
    gypsum: '#fafafc',
    white: '#ffffff'
  },
  tan: '#FFF9DA',
  focused: '#f7f4fb',
  focusedRed: '#EE5C3D',
  focusedRedWithAlpha: 'rgba(238,92,61,0.1)',
  lightPrimary: '#D2FFE4',
  lightestPrimary: '#EBF7F1',
  primary: '#0A342d',
  personal: '#6EDAF2',
  personal_green: '#0D342D',
  partner: '#F2D022',
  shared: '#98D3B6',
  sharedDarker: '#3EB579',
  personalLighter: '#B0E6F2',
  sharedLighter: '#98D386',
  partnerLighter: '#F1E08A',
  greenGradients: ['#195F53', '#0B2E28']
};
colors.borderColor = colors.grayscale.lightGray;
colors.green = colors.shared;

const borders = {
  divider: `1px solid ${colors.borderColor}`,
  error: `1px solid ${colors.dangerRed}`,
  primary: `1px solid ${colors.primary}`
};

const shadows = {
  modalOverlay:
    'rgba(40, 42, 52, 0.7) 0px 0px 0px 100vh, rgba(40, 42, 52, 0.7) 0px 0px 32px'
};

const space = [0, 4, 8, 12, 16, 24, 32, 48, 64, 128, 256, 512];

const fontFamily = {
  body: "'Open Sans', sans-serif"
};

const fontSizes = [12, 14, 16, 18, 26, 28, 42];

const fontWeights = {
  light: 300,
  regular: 400,
  semibold: 600,
  bold: 700
};

const textStyles = {
  displayXXLarge: {
    fontSize: '50px',
    lineHeight: '68px',
    letterSpacing: '0px'
  },
  displayXLarge: {
    fontSize: '42px',
    lineHeight: '44px',
    letterSpacing: '0px'
  },
  displayLargeHeader: {
    fontSize: '36px',
    lineHeight: '38px',
    letterSpacing: '0px'
  },
  displayLarge: {
    fontSize: '28px',
    lineHeight: '32px',
    letterSpacing: '0px'
  },
  displayMedium: {
    fontSize: '24px',
    lineHeight: '1',
    letterSpacing: '0px'
  },
  displaySmall: {
    fontSize: '20px',
    lineHeight: '26px',
    letterSpacing: '0px'
  },
  body: {
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0px'
  },
  caption: {
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0px'
  },
  disclaimer: {
    fontSize: '14px',
    lineHeight: 1.5
  },
  graphLabel: {
    color: colors.grayscale.black,
    fontSize: '8px',
    lineHeight: '12px',
    letterSpacing: '0px'
  }
};

// Not sure what we'd use this for yet
const colorStyles = {
  warning: {
    color: 'black',
    backgroundColor: 'orange'
  },
  error: {
    color: 'white',
    backgroundColor: 'red'
  }
};

const buttonBase = {
  border: 'none',
  borderRadius: '4px'
};

const buttonGroupButtonBase = {
  backgroundColor: colors.grayscale.white,
  fontSize: `${fontSizes[0]}px`,
  fontWeight: fontWeights.light,
  py: 2,
  px: 3,
  padding: `calc(${space[2]}px - 1px) ${space[4]}px calc(${space[2]}px - 2px)`
};

const buttons = {
  base: buttonBase,
  BAB: {
    ...buttonBase,
    color: colors.grayscale.white,
    backgroundColor: colors.grayscale.black,
    padding: `${space[4]}px ${space[7]}px`,
    width: '100%',
    fontWeight: fontWeights.bold,
    ...textStyles.displaySmall
  },
  FCQ: {
    ...buttonBase,
    color: colors.grayscale.white,
    backgroundColor: colors.primary,
    padding: `${space[4]}px ${space[6]}px`,
    fontWeight: fontWeights.light,
    ...textStyles.displaySmall
  },
  buttonGroupInactive: {
    ...buttonBase,
    ...buttonGroupButtonBase,
    color: colors.grayscale.stone,
    borderRadius: '0px',
    borderBottom: `2px solid transparent`
  },
  buttonGroupActive: {
    ...buttonBase,
    ...buttonGroupButtonBase,
    color: colors.grayscale.black,
    borderRadius: '0px',
    borderBottom: `2px solid ${colors.primary}`
  },
  cta: {
    ...buttonBase,
    backgroundColor: colors.black,
    color: colors.grayscale.white,
    fontSize: fontSizes[3],
    padding: `${space[4]}px ${space[8]}px`
  },
  form: {
    ...buttonBase,
    color: colors.grayscale.white,
    backgroundColor: colors.primary,
    padding: `${space[4]}px ${space[7]}px`,
    width: '100%',
    fontSize: '20px',
    fontWeight: fontWeights.bold,
    ...textStyles.displaySmall
  },
  formNoBg: {
    ...buttonBase,
    color: colors.primary,
    backgroundColor: 'transparent',
    padding: `${space[4]}px ${space[7]}px`,
    borderRadius: '4px',
    border: `1px solid ${colors.primary}`,
    width: '100%',
    fontSize: '20px',
    fontWeight: fontWeights.bold,
    ...textStyles.displaySmall
  },
  inverted: {
    ...buttonBase,
    color: colors.primary,
    backgroundColor: colors.lightestPrimary,
    padding: `${space[4]}px ${space[7]}px`,
    width: '100%',
    fontSize: '20px',
    fontWeight: fontWeights.bold,
    ...textStyles.displaySmall
  },
  select: {
    ...buttonBase,
    backgroundColor: colors.grayscale.white,
    color: colors.grayscale.black,
    border: borders.divider,
    fontWeight: fontWeights.light,
    textAlign: 'left',
    padding: `${space[2]}px ${space[3]}px`,
    width: '100%'
  },
  text: {
    ...buttonBase,
    ...textStyles.caption,
    backgroundColor: 'transparent',
    color: colors.grayscale.black,
    fontWeight: fontWeights.regular,
    padding: `${space[4]}px 0`
  },
  textUppercase: {
    ...buttonBase,
    ...textStyles.caption,
    backgroundColor: 'transparent',
    color: colors.grayscale.black,
    fontWeight: fontWeights.regular,
    padding: `${space[4]}px 0`,
    textTransform: 'uppercase'
  }
};
buttons.primary = {
  ...buttons.text,
  color: colors.primary,
  fontWeight: fontWeights.bold,
  textTransform: 'uppercase'
};

const theme = {
  breakpoints,
  space,

  borders,
  colors,
  shadows,

  fontFamily,
  fontSizes,
  fontWeights,

  maxWidths: {
    pageMaxWidth: breakpoints[breakpoints.length - 1]
  },

  // complex styles:
  colorStyles,
  textStyles,
  buttons
};

export default theme;
